import type { BlendSettings, ColourFinish, GlitterSettings } from '../types';

export interface FinishTypeOption {
  value: ColourFinish;
  label: string;
  description: string;
  sortOrder: number;
  defaultColourwayId?: string | null;
}

export type FinishBaseMode = 'shifting' | 'gradient' | 'single' | 'quick_change' | 'galaxy';

export interface FinishGlitterConfig {
  enabled: boolean;
  density: number;
  size: number;
  intensity: number;
}

export interface FinishQuickChangeConfig {
  bandCount: number;
  spiralWraps: number;
  transitionSharpness: number;
}

export interface FinishBlendDefaultsConfig {
  blendWidth: number;
  blendExponent: number;
  blendSteepness: number;
  blendAxis: 'horizontal' | 'vertical';
  blendMode: 'full' | 'centered';
  blendCenter: number;
  blendWindow: number;
  blendSpace: 'srgb' | 'oklab';
  seamOffset: number;
}

export interface FinishRenderSettings {
  glitter: FinishGlitterConfig;
  quickChange: FinishQuickChangeConfig;
  blendDefaults: FinishBlendDefaultsConfig;
}

export interface FinishTypeBehaviorConfig {
  baseFinishMode: string;
  renderSettings: FinishRenderSettings;
}

// Public-facing finish copy stays in code so the site can still render sensibly
// if the finish visibility table is missing or temporarily unavailable.
export const FINISH_TYPE_OPTIONS: FinishTypeOption[] = [
  {
    value: 'shifting',
    label: 'Shifting / Rotational Colour',
    description: 'Colours change as the vase turns - a magical, transforming effect.',
    sortOrder: 10,
  },
  {
    value: 'gradient',
    label: 'Vertical Gradient',
    description: 'Sections of colour going from top to bottom.',
    sortOrder: 20,
  },
  {
    value: 'single',
    label: 'Single Colour',
    description: 'One consistent shade throughout the vase.',
    sortOrder: 30,
  },
  {
    value: 'quick_change',
    label: 'Quick Change',
    description: 'A rapidly shifting gradient with bold, unpredictable colour transitions.',
    sortOrder: 40,
  },
  {
    value: 'galaxy',
    label: 'Galaxy',
    description: 'Deep, rich tones with subtle glitter highlights for a cosmic-inspired shimmer.',
    sortOrder: 50,
  },
];

export const DEFAULT_PUBLIC_FINISH_KEYS: ColourFinish[] = ['shifting', 'gradient', 'single'];
export const DEFAULT_INTRO_FINISH_KEYS: ColourFinish[] = ['shifting', 'gradient', 'single'];

export const FINISH_BASE_MODE_OPTIONS: Array<{ value: FinishBaseMode; label: string; description: string }> = [
  { value: 'single', label: 'Single Colour', description: 'One colour across the vase.' },
  { value: 'gradient', label: 'Gradient', description: 'Vertical colour transitions.' },
  { value: 'shifting', label: 'Shifting', description: 'Rotational / directional colour changes.' },
  { value: 'quick_change', label: 'Quick Change', description: 'Rapid repeated banding / spiral variation.' },
  { value: 'galaxy', label: 'Galaxy', description: 'Single/shifted look with glitter sparkle effect.' },
];

const KNOWN_BASE_MODES = new Set<FinishBaseMode>(FINISH_BASE_MODE_OPTIONS.map(option => option.value));

export const DEFAULT_FINISH_RENDER_SETTINGS: FinishRenderSettings = {
  glitter: {
    enabled: false,
    density: 0.35,
    size: 0.35,
    intensity: 0.4,
  },
  quickChange: {
    bandCount: 8,
    spiralWraps: 2.5,
    transitionSharpness: 0.65,
  },
  blendDefaults: {
    blendWidth: 0.6,
    blendExponent: 1.0,
    blendSteepness: 1.0,
    blendAxis: 'vertical',
    blendMode: 'full',
    blendCenter: 0,
    blendWindow: 0.6,
    blendSpace: 'srgb',
    seamOffset: 0,
  },
};

const FINISH_OPTION_MAP = new Map<string, FinishTypeOption>(
  FINISH_TYPE_OPTIONS.map(option => [option.value, option])
);

export const getFinishTypeOption = (key: string): FinishTypeOption | null => {
  return FINISH_OPTION_MAP.get(key) ?? null;
};

export const getFinishTypeLabel = (key: string): string => {
  return FINISH_OPTION_MAP.get(key)?.label ?? key;
};

export const formatFinishTypeKeyLabel = (key: string): string => {
  if (!key) return '';
  return key
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export const inferBaseFinishMode = (key: string): FinishBaseMode => {
  return (KNOWN_BASE_MODES.has(key as FinishBaseMode) ? key : 'single') as FinishBaseMode;
};

export const getDefaultBlendDefaultsForMode = (mode: FinishBaseMode): FinishBlendDefaultsConfig => {
  switch (mode) {
    case 'shifting':
      return {
        blendWidth: 0.8,
        blendExponent: 0.64,
        blendSteepness: 1.0,
        blendAxis: 'horizontal',
        blendMode: 'full',
        blendCenter: 0,
        blendWindow: 0.6,
        blendSpace: 'oklab',
        seamOffset: 0,
      };
    case 'quick_change':
      return {
        blendWidth: 0.35,
        blendExponent: 1.0,
        blendSteepness: 5.0,
        blendAxis: 'vertical',
        blendMode: 'full',
        blendCenter: 0,
        blendWindow: 0.3,
        blendSpace: 'oklab',
        seamOffset: 0,
      };
    case 'gradient':
      return {
        blendWidth: 0.6,
        blendExponent: 1.0,
        blendSteepness: 1.0,
        blendAxis: 'vertical',
        blendMode: 'full',
        blendCenter: 0,
        blendWindow: 0.6,
        blendSpace: 'srgb',
        seamOffset: 0,
      };
    case 'galaxy':
      return {
        blendWidth: 0.6,
        blendExponent: 1.0,
        blendSteepness: 1.0,
        blendAxis: 'vertical',
        blendMode: 'full',
        blendCenter: 0,
        blendWindow: 0.6,
        blendSpace: 'srgb',
        seamOffset: 0,
      };
    case 'single':
    default:
      return {
        blendWidth: 0.6,
        blendExponent: 1.0,
        blendSteepness: 1.0,
        blendAxis: 'vertical',
        blendMode: 'full',
        blendCenter: 0,
        blendWindow: 0.6,
        blendSpace: 'srgb',
        seamOffset: 0,
      };
  }
};

export const normalizeFinishRenderSettings = (
  value: unknown,
  finishKey?: string,
  baseFinishMode?: string
): FinishRenderSettings => {
  const source = (value && typeof value === 'object') ? (value as Record<string, any>) : {};
  const glitterSource = (source.glitter && typeof source.glitter === 'object')
    ? (source.glitter as Record<string, any>)
    : {};
  const quickChangeSource = (source.quickChange && typeof source.quickChange === 'object')
    ? (source.quickChange as Record<string, any>)
    : {};
  const blendDefaultsSource = (source.blendDefaults && typeof source.blendDefaults === 'object')
    ? (source.blendDefaults as Record<string, any>)
    : {};

  const inferredMode = inferBaseFinishMode(baseFinishMode || finishKey || 'single');
  const glitterEnabledDefault = finishKey === 'galaxy' || inferredMode === 'galaxy';
  const quickChangeDefault = finishKey === 'quick_change' || inferredMode === 'quick_change';
  const modeBlendDefaults = getDefaultBlendDefaultsForMode(inferredMode);

  return {
    glitter: {
      enabled: typeof glitterSource.enabled === 'boolean' ? glitterSource.enabled : glitterEnabledDefault,
      density: clamp(Number.isFinite(glitterSource.density) ? Number(glitterSource.density) : DEFAULT_FINISH_RENDER_SETTINGS.glitter.density, 0, 1),
      size: clamp(Number.isFinite(glitterSource.size) ? Number(glitterSource.size) : DEFAULT_FINISH_RENDER_SETTINGS.glitter.size, 0, 1),
      intensity: clamp(Number.isFinite(glitterSource.intensity) ? Number(glitterSource.intensity) : (glitterEnabledDefault ? 0.55 : DEFAULT_FINISH_RENDER_SETTINGS.glitter.intensity), 0, 1),
    },
    quickChange: {
      bandCount: clamp(
        Math.round(Number.isFinite(quickChangeSource.bandCount) ? Number(quickChangeSource.bandCount) : (quickChangeDefault ? 14 : DEFAULT_FINISH_RENDER_SETTINGS.quickChange.bandCount)),
        1,
        64
      ),
      spiralWraps: clamp(
        Number.isFinite(quickChangeSource.spiralWraps) ? Number(quickChangeSource.spiralWraps) : (quickChangeDefault ? 5 : DEFAULT_FINISH_RENDER_SETTINGS.quickChange.spiralWraps),
        0,
        20
      ),
      transitionSharpness: clamp(
        Number.isFinite(quickChangeSource.transitionSharpness) ? Number(quickChangeSource.transitionSharpness) : (quickChangeDefault ? 0.8 : DEFAULT_FINISH_RENDER_SETTINGS.quickChange.transitionSharpness),
        0,
        1
      ),
    },
    blendDefaults: {
      blendWidth: clamp(
        Number.isFinite(blendDefaultsSource.blendWidth) ? Number(blendDefaultsSource.blendWidth) : modeBlendDefaults.blendWidth,
        0,
        1
      ),
      blendExponent: clamp(
        Number.isFinite(blendDefaultsSource.blendExponent) ? Number(blendDefaultsSource.blendExponent) : modeBlendDefaults.blendExponent,
        0.05,
        4
      ),
      blendSteepness: clamp(
        Number.isFinite(blendDefaultsSource.blendSteepness) ? Number(blendDefaultsSource.blendSteepness) : modeBlendDefaults.blendSteepness,
        0.5,
        20
      ),
      blendAxis: blendDefaultsSource.blendAxis === 'horizontal' ? 'horizontal' : modeBlendDefaults.blendAxis,
      blendMode: blendDefaultsSource.blendMode === 'centered' ? 'centered' : modeBlendDefaults.blendMode,
      blendCenter: clamp(
        Number.isFinite(blendDefaultsSource.blendCenter) ? Number(blendDefaultsSource.blendCenter) : modeBlendDefaults.blendCenter,
        -0.5,
        0.5
      ),
      blendWindow: clamp(
        Number.isFinite(blendDefaultsSource.blendWindow) ? Number(blendDefaultsSource.blendWindow) : modeBlendDefaults.blendWindow,
        0.05,
        1
      ),
      blendSpace: blendDefaultsSource.blendSpace === 'oklab' ? 'oklab' : modeBlendDefaults.blendSpace,
      seamOffset: clamp(
        Number.isFinite(blendDefaultsSource.seamOffset) ? Number(blendDefaultsSource.seamOffset) : modeBlendDefaults.seamOffset,
        0,
        1
      ),
    },
  };
};

export const createDefaultFinishBehaviorConfig = (finishKey: string): FinishTypeBehaviorConfig => {
  const baseFinishMode = inferBaseFinishMode(finishKey);
  return {
    baseFinishMode,
    renderSettings: normalizeFinishRenderSettings(undefined, finishKey, baseFinishMode),
  };
};

type PreviewCombinationLike = {
  finish_type?: string;
  blend_width?: number | null;
  blend_axis?: 'horizontal' | 'vertical' | null;
  blend_mode?: 'full' | 'centered' | null;
  blend_center?: number | null;
  blend_window?: number | null;
  blend_space?: 'srgb' | 'oklab' | null;
  blend_seam_offset?: number | null;
  blend_steepness?: number | null;
  blend_exponent?: number | null;
  glitter_intensity?: number | null;
};

export const resolveFinishBlendDefaults = (
  finishKey: string,
  baseFinishMode?: string,
  renderSettings?: FinishRenderSettings | null
): FinishBlendDefaultsConfig => {
  const normalized = normalizeFinishRenderSettings(renderSettings, finishKey, baseFinishMode ?? finishKey);
  return normalized.blendDefaults;
};

export const buildGlitterSettingsForPreview = (
  finishKey: string,
  baseFinishMode: string,
  renderSettings?: FinishRenderSettings | null,
  comboGlitterIntensity?: number | null
): (GlitterSettings & { enabled: boolean }) => {
  const normalized = normalizeFinishRenderSettings(renderSettings, finishKey, baseFinishMode);
  const comboScale = comboGlitterIntensity == null ? 1 : clamp(comboGlitterIntensity, 0, 1);
  const sparkleSpeed = 0.25 + normalized.glitter.intensity * 0.75;
  return {
    enabled: normalized.glitter.enabled,
    density: normalized.glitter.density,
    size: normalized.glitter.size,
    intensity: clamp(normalized.glitter.intensity * comboScale, 0, 1),
    sparkleSpeed,
  };
};

export const buildBlendSettingsForPreview = (
  finishKey: string,
  baseFinishMode: string,
  renderSettings: FinishRenderSettings | null | undefined,
  combo?: PreviewCombinationLike | null
): Partial<BlendSettings> | undefined => {
  const normalized = normalizeFinishRenderSettings(renderSettings, finishKey, baseFinishMode);
  const defaults = normalized.blendDefaults;
  const mode = inferBaseFinishMode(baseFinishMode || finishKey);

  const blendWidth = combo?.blend_width ?? defaults.blendWidth;
  const blendAxis = combo?.blend_axis ?? defaults.blendAxis;
  const blendMode = combo?.blend_mode ?? defaults.blendMode;
  const blendCenter = combo?.blend_center ?? defaults.blendCenter;
  const blendWindow = combo?.blend_window ?? defaults.blendWindow;
  const blendSpace = combo?.blend_space ?? defaults.blendSpace;
  const seamOffset = combo?.blend_seam_offset ?? defaults.seamOffset;
  const blendSteepness = combo?.blend_steepness ?? defaults.blendSteepness;
  const blendExponent = combo?.blend_exponent ?? defaults.blendExponent;

  if (mode === 'single' || mode === 'galaxy') {
    return undefined;
  }

  if (mode === 'quick_change') {
    const softness = clamp(1 - normalized.quickChange.transitionSharpness, 0.02, 0.95);
    return {
      blendWidth: combo?.blend_width ?? softness,
      streakIntensity: 0,
      blendCurve: 'smoothstep',
      edgeHardness: normalized.quickChange.transitionSharpness,
      blendExponent,
      blendSteepness,
      blendAxis,
      blendMode,
      blendCenter,
      blendWindow,
      blendSpace,
      seamOffset,
      quickBandCount: normalized.quickChange.bandCount,
      quickSpiralWraps: normalized.quickChange.spiralWraps,
      quickTransitionSharpness: normalized.quickChange.transitionSharpness,
    } as Partial<BlendSettings>;
  }

  return {
    blendWidth,
    streakIntensity: 0,
    blendCurve: 'smoothstep',
    edgeHardness: mode === 'gradient' ? 1 - clamp(blendWidth, 0, 1) : 0.5,
    blendExponent,
    blendSteepness,
    blendAxis,
    blendMode,
    blendCenter,
    blendWindow,
    blendSpace,
    seamOffset,
  } as Partial<BlendSettings>;
};

export const mapFinishKeysToOptions = (
  keys: string[],
  sortOverrides: Record<string, number> = {}
): FinishTypeOption[] => {
  return keys
    .map(key => {
      const option = getFinishTypeOption(key);
      if (!option) return null;
      const overrideSort = sortOverrides[key];
      return typeof overrideSort === 'number'
        ? { ...option, sortOrder: overrideSort }
        : option;
    })
    .filter((option): option is FinishTypeOption => option !== null)
    .sort((a, b) => (a.sortOrder - b.sortOrder) || a.label.localeCompare(b.label));
};
